import React, { useMemo, useState } from "react";
import {
  MANUFACTURING_MATERIAL_OPTIONS,
  getMaterialEngineeringProperties,
} from "../../data/machiningMaterials";

const format = (value, unit) =>
  `${Number(value).toFixed(unit === "metric" ? 3 : 4)} ${unit === "metric" ? "mm" : "in"}`;

const classifyFit = (allowance, desiredType) => {
  if (allowance > 0) return { classification: "Clearance Fit", pressFit: "No" };
  if (allowance < 0) return {
    classification: desiredType === "press" ? "Press Fit (Interference)" : "Interference Fit",
    pressFit: desiredType === "press" ? "Yes - press assembly selected" : "Possible - assembly method must be defined",
  };
  return { classification: "Line-to-Line / Transition Boundary", pressFit: "No deterministic clearance" };
};

export const calculateShaftHoleFit = (params) => {
  if (params.holeDiameter === "" || params.shaftDiameter === "") return null;
  const hole = Number(params.holeDiameter);
  const shaft = Number(params.shaftDiameter);
  const desired = params.desiredAllowance === "" ? null : Number(params.desiredAllowance);
  if (![hole, shaft].every(Number.isFinite) || hole <= 0 || shaft <= 0) return { error: "Hole and shaft diameters must be valid positive dimensions." };
  if (desired !== null && !Number.isFinite(desired)) return { error: "Desired clearance/interference must be numeric." };

  const allowance = hole - shaft;
  const actualClearance = Math.max(0, allowance);
  const actualInterference = Math.max(0, -allowance);
  const fit = classifyFit(allowance, params.fitType);
  const warnings = [];
  const diameterBasis = Math.max(hole, shaft);
  const interferenceRatio = actualInterference / diameterBasis;
  if (params.fitType === "clearance" && allowance <= 0) warnings.push("The entered dimensions do not produce the selected clearance fit.");
  if (["interference", "press"].includes(params.fitType) && allowance >= 0) warnings.push("The entered dimensions do not produce interference.");
  if (desired !== null && Math.abs(allowance - desired) > (params.unit === "metric" ? 0.01 : 0.0005)) warnings.push("Actual allowance differs from the desired value. Check the drawing limits and process capability.");
  if (interferenceRatio > 0.004) warnings.push("Interference exceeds 0.4% of mating diameter. This is unusually severe for many machined assemblies and requires engineering review.");

  const holeProperties = getMaterialEngineeringProperties(params.holeMaterial);
  const alpha = Number(holeProperties?.thermalExpansionPpmC) * 1e-6;
  const thermalDeltaC = actualInterference > 0 && alpha > 0
    ? actualInterference / (hole * alpha)
    : 0;

  return {
    ...fit,
    allowance,
    actualClearance,
    actualInterference,
    desired,
    interferenceRatio,
    thermalDeltaC,
    warnings,
    installationGuidance: allowance > 0
      ? "Positive clearance is available. Verify the required locating, running, lubrication, and alignment behavior."
      : allowance < 0
        ? "Use a controlled press or engineered thermal assembly method. Verify lead-in, surface finish, lubrication policy, wall thickness, stress, and component manufacturer requirements."
        : "Line-to-line nominal dimensions can assemble differently as real parts vary. Define limits before releasing the fit.",
  };
};

const FitAnalysisPanel = ({ onSave }) => {
  const [params, setParams] = useState({
    unit: "inch",
    holeDiameter: "",
    shaftDiameter: "",
    desiredAllowance: "",
    fitType: "clearance",
    holeMaterial: "mild_steel",
    shaftMaterial: "mild_steel",
  });
  const update = (field, value) => setParams((current) => ({ ...current, [field]: value }));

  const result = useMemo(() => calculateShaftHoleFit(params), [params]);

  const save = () => {
    if (!result || result.error || !onSave) return;
    onSave({
      summary: `${result.classification}: hole ${format(params.holeDiameter, params.unit)}, shaft ${format(params.shaftDiameter, params.unit)}`,
      input: {
        unit: params.unit,
        fitType: params.fitType,
        holeDiameter: format(params.holeDiameter, params.unit),
        shaftDiameter: format(params.shaftDiameter, params.unit),
        desiredAllowance: params.desiredAllowance === "" ? "Not specified" : format(params.desiredAllowance, params.unit),
        holeMaterial: params.holeMaterial,
        shaftMaterial: params.shaftMaterial,
      },
      results: {
        fitClassification: result.classification,
        pressFitClassification: result.pressFit,
        signedAllowance: format(result.allowance, params.unit),
        actualClearance: format(result.actualClearance, params.unit),
        actualInterference: format(result.actualInterference, params.unit),
        installationGuidance: result.installationGuidance,
        thermalGuidance: result.actualInterference > 0
          ? `Free-expansion estimate for hole material alone: approximately ${result.thermalDeltaC.toFixed(1)} C temperature rise. Engineering verification required.`
          : "Thermal assembly is not required by the entered dimensions.",
      },
      warnings: result.warnings,
      confidence: "Limits Required For Final Acceptance",
    });
  };

  return (
    <section className="fit-analysis-panel">
      <div className="fit-analysis-heading">
        <div><span className="shop-kicker">Shaft and Hole Fit Analysis</span><h2>Clearance, transition, interference, and press fits</h2><p>Enter the actual mating diameters. Positive hole-minus-shaft allowance is clearance; negative allowance is interference.</p></div>
        <select value={params.unit} onChange={(event) => update("unit", event.target.value)}><option value="inch">Inch</option><option value="metric">Metric</option></select>
      </div>
      <div className="fit-analysis-inputs">
        <label>Fit type<select value={params.fitType} onChange={(event) => update("fitType", event.target.value)}><option value="clearance">Clearance Fit</option><option value="transition">Transition Fit</option><option value="interference">Interference Fit</option><option value="press">Press Fit</option></select></label>
        <label>Hole diameter<input type="number" step="any" value={params.holeDiameter} onChange={(event) => update("holeDiameter", event.target.value)} placeholder="0.9990" /></label>
        <label>Shaft diameter<input type="number" step="any" value={params.shaftDiameter} onChange={(event) => update("shaftDiameter", event.target.value)} placeholder="1.0000" /></label>
        <label>Desired clearance (+) / interference (-)<input type="number" step="any" value={params.desiredAllowance} onChange={(event) => update("desiredAllowance", event.target.value)} placeholder="-0.0010" /></label>
        <label>Hole material<select value={params.holeMaterial} onChange={(event) => update("holeMaterial", event.target.value)}>{MANUFACTURING_MATERIAL_OPTIONS.map((material) => <option key={material.id} value={material.id}>{material.label}</option>)}</select></label>
        <label>Shaft material<select value={params.shaftMaterial} onChange={(event) => update("shaftMaterial", event.target.value)}>{MANUFACTURING_MATERIAL_OPTIONS.map((material) => <option key={material.id} value={material.id}>{material.label}</option>)}</select></label>
      </div>
      {!result && <p className="shop-muted">More data required: enter hole and shaft diameters.</p>}
      {result?.error && <p className="fit-analysis-error">{result.error}</p>}
      {result && !result.error && (
        <div className="fit-analysis-results">
          <div><span>Fit classification</span><strong>{result.classification}</strong></div>
          <div><span>Press fit</span><strong>{result.pressFit}</strong></div>
          <div><span>Actual clearance</span><strong>{format(result.actualClearance, params.unit)}</strong></div>
          <div><span>Actual interference</span><strong>{format(result.actualInterference, params.unit)}</strong></div>
          <p>{result.installationGuidance}</p>
          {result.actualInterference > 0 && <p>Thermal fit estimate: freely heating the selected hole material alone would require about {result.thermalDeltaC.toFixed(1)} C of uniform temperature rise to expand by the nominal interference. Add assembly margin and verify both materials, geometry, coatings, seals, heat treatment, and safe temperature limits.</p>}
          {result.warnings.map((warning) => <p key={warning} className="fit-analysis-error">{warning}</p>)}
          <small>Formula: signed allowance = hole diameter - shaft diameter. Positive = clearance; negative = interference. Final fit acceptance must use drawing limits or the applicable ISO 286 / ANSI B4.1 fit designation.</small>
          {onSave && <button type="button" className="btn btn-primary" onClick={save}>Save Fit to Manufacturing Plan</button>}
        </div>
      )}
    </section>
  );
};

export default FitAnalysisPanel;
